import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Form, Input, Link, Popconfirm, Select, Table } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import useTable from '@/hooks/useTable';
import { deletePermission, getGrants } from '@/services/kafkaPermissionsService';
import ShadowCard from '@/components/ShadowCard';
import PermWrapper from '@/components/PermWrapper';
import { toTsOptions } from '@/utils/common';
import { fetchResourceTypeOptions } from '@/slices/kafkaPermissionsSlice';
import DisableBuriedPermWrapper from '@/components/DisableWrapper/DisableBuriedPermWrapper';
import NoAuthAlert from '../../components/NoAuthAlert';
import Modal from './Modal';
import Summary from './Summary';

const { FormItem } = Form;

function Grant({ options, fetchResourceTypeOptions }) {
  const [searchParams] = useSearchParams();
  const clusterName = searchParams.get('name');
  const [visible, setVisible] = useState(false);

  // 查询初始化
  const initialData = useMemo(
    () => ({
      principal: searchParams.get('roleName'),
    }),
    [searchParams],
  );

  const columns = [
    {
      colKey: 'principal',
      title: 'Principal',
      width: 180,
    },

    {
      colKey: 'permission',
      title: 'Permission',
      width: 140,
    },
    {
      colKey: 'patternType',
      title: 'Pattern Type',
      width: 140,
    },
    {
      colKey: 'operations',
      title: 'Operations',
      width: 180,
    },
    {
      colKey: 'resource',
      title: 'Resource',
    },
    {
      colKey: 'host',
      title: 'Host',
      width: 200,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Popconfirm theme='danger' content='确定删除？' onConfirm={() => handleDel(row)}>
          <DisableBuriedPermWrapper resource='kafka:auth:grant:delete'>
            <Link theme='danger' hover='color'>
              移除
            </Link>
          </DisableBuriedPermWrapper>
        </Popconfirm>
      ),
      width: 120,
    },
  ];

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getGrants({ ...params, clusterName });
    setData(data);
  });

  const { header, config, setData, doRefresh, doSearch, form } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <>
        <FormItem name='principal'>
          <Input placeholder='Principal' clearable />
        </FormItem>
        <FormItem name='resourceType'>
          <Select options={toTsOptions(options)} placeholder='选择 Resource Type' clearable />
        </FormItem>
        <FormItem name='resource'>
          <Input placeholder='请输入 Resource' clearable />
        </FormItem>
      </>
    ),
    operators: (
      <PermWrapper resource='kop:auth:grant:create'>
        <Button
          theme='primary'
          onClick={() => {
            setVisible(true);
          }}
        >
          添加ACL
        </Button>
      </PermWrapper>
    ),
  });

  const doInit = useMemoizedFn(async () => {
    fetchResourceTypeOptions(clusterName);
    // 有初始化条件
    if (searchParams.size > 1) {
      await doSearch(initialData);
      form.setFieldsValue(initialData);
    } else {
      doSearch();
    }
  });

  useEffect(() => {
    if (clusterName) {
      doInit();
    }
  }, [clusterName, doInit]);

  const handleDel = async (row) => {
    const [resourceType, resourceName] = row.resource.split(' ');
    await deletePermission({
      clusterName,
      patternType: row.patternType,
      resourceType,
      resourceName,
      aclPermissionType: row.permission,
      aclOperation: row.operations,
      host: row.host,
      principal: row.principal,
    });
    doRefresh();
  };

  const handleClose = (refresh) => {
    setVisible(false);
    if (refresh) {
      // 刷新
      doRefresh();
    }
  };

  return (
    <Root>
      <Summary />
      <NoAuthAlert name='授权' />
      <Body>
        {header}
        <Table {...config} />
        <Modal clusterName={clusterName} visible={visible} handleClose={handleClose} />
      </Body>
    </Root>
  );
}

export default connect(
  (state) => ({
    options: state.kafkaPermissions.resourceTypeOptions,
  }),
  {
    fetchResourceTypeOptions,
  },
)(Grant);

const Root = styled.div`
  padding-right: 20px;
`;

const Body = styled(ShadowCard)`
  margin-top: 14px;
  .t-form-inline .t-form__item {
    margin-right: var(--td-comp-margin-s);
  }
`;
